import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { IMAGES } from "../constants/theme";
import { useAuth } from "../auth/AuthContext";
import apiClient from "../api/apiClient";
import "./AuthPage.css";

const SurveyGameDialog = ({ quiz, onClose }) => {
  const [answers, setAnswers] = useState({});
  const [feedback, setFeedback] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const allAnswered = quiz.questions.every((question) => answers[question.id] !== undefined);

  const chooseAnswer = (question, answerIndex) => {
    if (result) return;

    setAnswers((current) => ({ ...current, [question.id]: answerIndex }));
    setFeedback((current) => ({ ...current, [question.id]: "selected" }));
  };

  const submitQuiz = async () => {
    setError("");
    setSubmitting(true);

    try {
      const { data } = await apiClient.post("/survey-game/submit", {
        answers: quiz.questions.map((question) => ({
          question_id: question.id,
          answer_index: answers[question.id],
        })),
      });

      setResult(data);
      setFeedback(
        Object.fromEntries(
          data.results.map((item) => [item.question_id, item.is_correct ? "right" : "wrong"]),
        ),
      );
    } catch (err) {
      setError(err.response?.data?.message || "Could not submit your answers. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="survey-game-backdrop" role="dialog" aria-modal="true">
      <div className={`survey-game-dialog ${result?.won ? "survey-game-won" : ""}`}>
        <div className="survey-game-header">
          <span>First Month Challenge</span>
          <h3>Answer all 5 correctly to win a free subscription.</h3>
          <p>{result ? `You scored ${result.score}/${result.total}.` : "Five basic gym questions. One shot. Trust your training brain."}</p>
        </div>

        <div className="survey-game-questions">
          {quiz.questions.map((question, questionIndex) => {
            const resultItem = result?.results?.find((item) => item.question_id === question.id);

            return (
              <div className={`survey-game-question survey-game-${feedback[question.id] || "idle"}`} key={question.id}>
                <div className="survey-game-question-title">
                  <span>{questionIndex + 1}</span>
                  <strong>{question.question}</strong>
                </div>
                <div className="survey-game-options">
                  {question.options.map((option, optionIndex) => {
                    const isSelected = answers[question.id] === optionIndex;
                    const isCorrect = resultItem?.correct_index === optionIndex;
                    const isWrongSelection = resultItem && isSelected && !isCorrect;

                    return (
                      <button
                        type="button"
                        key={option}
                        className={[
                          "survey-game-option",
                          isSelected ? "selected" : "",
                          resultItem && isCorrect ? "correct" : "",
                          isWrongSelection ? "wrong" : "",
                        ].join(" ")}
                        onClick={() => chooseAnswer(question, optionIndex)}
                        disabled={!!result}
                      >
                        {option}
                      </button>
                    );
                  })}
                </div>
                {resultItem && (
                  <div className="survey-game-answer">
                    Correct answer: <strong>{resultItem.correct_answer}</strong>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {error && <div className="auth-error">{error}</div>}

        {result ? (
          <div className="survey-game-result">
            <h4>{result.won ? "You won your free first month!" : "Nice try. Your answers are shown above."}</h4>
            <p>
              {result.won
                ? "Your one-month subscription has been activated on your account."
                : "The game is complete, and the correct answers are now visible for review."}
            </p>
            <button type="button" className="btn btn-primary btn-skew" onClick={onClose}>
              <span>Continue</span>
            </button>
          </div>
        ) : (
          <button
            type="button"
            className={`btn btn-primary btn-skew w-100 survey-game-submit ${submitting ? "auth-submit-loading" : ""}`}
            onClick={submitQuiz}
            disabled={!allAnswered || submitting}
          >
            <span>{submitting ? "Checking answers" : "Submit Answers"}</span>
          </button>
        )}
      </div>
    </div>
  );
};

const AuthPage = () => {
  const [mode, setMode] = useState("login");
  const navigate = useNavigate();

  const { login, register } = useAuth();

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
    password_confirmation: "",
    country: "",
    city: "",
    street: "",
    building: "",
    trainings_per_week: ""
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [quiz, setQuiz] = useState(null);

  const handleChange = (e) => {
    setForm({
      ...form,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setError("");
    setLoading(true);

    try {
      if (mode === "login") {
        await login(form.email, form.password);
        navigate("/home-2");
      } else {
        await register({
          name: form.name,
          email: form.email,
          phone: form.phone,
          password: form.password,
          password_confirmation: form.password_confirmation,
          country: form.country,
          city: form.city,
          street: form.street,
          building: form.building,
          trainings_per_week: form.trainings_per_week
        });

        const { data } = await apiClient.get("/survey-game");

        if (data.available) {
          setQuiz(data);
        } else {
          navigate("/home-2");
        }
      }
    } catch (err) {
      setError(err.response?.data?.message || "Authentication failed");
    }

    setLoading(false);
  };

  return (
    <div className="page-wraper auth-page-wrap">
      <div
        className="page-content auth-page-content"
        style={{
          backgroundImage: `url(${IMAGES.AuthPerformanceBg})`
        }}
      >
        <section className="appointment-page auth-hero">
          <div
            className={`container auth-shell ${
              mode === "login" ? "auth-shell-login" : "auth-shell-signup"
            }`}
          >
            <div
              className={`auth-card ${
                mode === "signup" ? "auth-card-signup" : ""
              }`}
            >
              <div className="auth-logo-wrap">
                <img src={IMAGES.logo} alt="Hybrid Performance Academy" className="auth-logo hpa-auth-logo" />
              </div>

              <div className="section-head text-center mb-4">
                <h3 className="title mb-0">
                  {mode === "login"
                    ? "Welcome Back"
                    : "Create Your Account"}
                </h3>
              </div>

              {/* Mode Switch */}
              <div className="auth-mode-switch">
                <button
                  type="button"
                  onClick={() => setMode("login")}
                  className={`auth-mode-btn ${
                    mode === "login" ? "active" : ""
                  }`}
                >
                  Login
                </button>

                <button
                  type="button"
                  onClick={() => setMode("signup")}
                  className={`auth-mode-btn ${
                    mode === "signup" ? "active" : ""
                  }`}
                >
                  Sign Up
                </button>
              </div>

              {error && (
                <div className="auth-error">
                  {error}
                </div>
              )}

              <form
                onSubmit={handleSubmit}
                className={`auth-form ${mode === "signup" ? "auth-form-signup" : ""}`}
              >
                {mode === "signup" && (
                  <div className="auth-signup-grid">
                    <div className="form-group mb-3">
                      <input
                        type="text"
                        name="name"
                        className="form-control"
                        placeholder="Full Name"
                        required
                        onChange={handleChange}
                      />
                    </div>

                    <div className="form-group mb-3">
                      <input
                        type="text"
                        name="phone"
                        className="form-control"
                        placeholder="Phone Number"
                        required
                        onChange={handleChange}
                      />
                    </div>

                    <div className="form-group mb-3">
                      <input
                        type="text"
                        name="country"
                        className="form-control"
                        placeholder="Country"
                        onChange={handleChange}
                      />
                    </div>

                    <div className="form-group mb-3">
                      <input
                        type="text"
                        name="city"
                        className="form-control"
                        placeholder="City"
                        onChange={handleChange}
                      />
                    </div>

                    <div className="form-group mb-3">
                      <input
                        type="text"
                        name="street"
                        className="form-control"
                        placeholder="Street"
                        onChange={handleChange}
                      />
                    </div>

                    <div className="form-group mb-3">
                      <input
                        type="text"
                        name="building"
                        className="form-control"
                        placeholder="Building"
                        onChange={handleChange}
                      />
                    </div>

                    <div className="form-group mb-3">
                      <select
                        name="trainings_per_week"
                        className="form-control"
                        onChange={handleChange}
                      >
                        <option value="">
                          Training Sessions Per Week
                        </option>
                        <option value="1">1</option>
                        <option value="2">2</option>
                        <option value="3">3</option>
                        <option value="4">4</option>
                        <option value="5">5</option>
                        <option value="6">6</option>
                        <option value="7">7</option>
                      </select>
                    </div>
                  </div>
                )}

                <div className="form-group mb-3">
                  <input
                    type="email"
                    name="email"
                    className="form-control"
                    placeholder="Email Address"
                    required
                    onChange={handleChange}
                  />
                </div>

                <div className="form-group mb-3">
                  <input
                    type="password"
                    name="password"
                    className="form-control"
                    placeholder="Password"
                    required
                    onChange={handleChange}
                  />
                </div>

                {mode === "signup" && (
                  <div className="form-group mb-3">
                    <input
                      type="password"
                      name="password_confirmation"
                      className="form-control"
                      placeholder="Confirm Password"
                      required
                      onChange={handleChange}
                    />
                  </div>
                )}

                {mode === "login" && (
                  <div className="auth-forgot">
                    <Link to="/forgot-password">
                      Forgot password?
                    </Link>
                  </div>
                )}

                <button
                  type="submit"
                  className={`btn btn-primary btn-lg btn-skew w-100 auth-submit ${
                    loading ? "auth-submit-loading" : ""
                  }`}
                  disabled={loading}
                >
                  <span>
                    {loading
                      ? mode === "login"
                        ? "Logging in"
                        : "Creating account"
                      : mode === "login"
                      ? "Login"
                      : "Create Account"}
                  </span>
                </button>
              </form>

              <p className="text-center mt-4 mb-0 auth-switch-text">
                {mode === "login" ? (
                  <>
                    Don’t have an account?{" "}
                    <span
                      className="auth-switch-link"
                      onClick={() => setMode("signup")}
                    >
                      Sign up
                    </span>
                  </>
                ) : (
                  <>
                    Already have an account?{" "}
                    <span
                      className="auth-switch-link"
                      onClick={() => setMode("login")}
                    >
                      Login
                    </span>
                  </>
                )}
              </p>
            </div>
          </div>
          {quiz && (
            <SurveyGameDialog
              quiz={quiz}
              onClose={() => {
                setQuiz(null);
                navigate("/home-2");
              }}
            />
          )}
        </section>
      </div>
    </div>
  );
};

export default AuthPage;
